import type { IChatAssistantGroup } from "@frontend-shared/types";
import { formatChatAssistantGroupLabel } from "@frontend-shared/utils";
import { memo, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { AssistantAvatar } from "@/components/AssistantAvatar";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useCollapsedSidebarSections } from "@/hooks/use-collapsed-sidebar-sections";

import { useSidebarInfiniteScroll } from "../../hooks/use-sidebar-infinite-scroll";
import { AssistantChatGroupLabel } from "./AssistantChatGroupLabel";
import { ChatListItem } from "./ChatListItem";
import { ChatListSkeleton } from "./ChatListSkeleton";
import { SidebarGroupCount } from "./SidebarGroupCount";

const ASSISTANT_GROUP_AVATAR_SIZE_PX = 16;

interface IAssistantChatGroupsProps {
  groups: IChatAssistantGroup[];
  // NOTE: The list payload carries no avatars (they are data URLs of up to
  // 512KB); the sidebar resolves them from the assistants it already holds.
  assistantAvatarByUuid: Record<string, string | null>;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => unknown;
}

// NOTE: NSAI-5678 — the body of the sidebar's "Assistants" section: one
// collapsible group per assistant, fed by the assistant-kind chat list, which
// pages on its own so an old assistant chat is reachable here without scrolling
// the whole regular list to it.
const AssistantChatGroupsInner = ({
  groups,
  assistantAvatarByUuid,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: IAssistantChatGroupsProps): React.ReactElement => {
  const { t } = useTranslation("sidebar");
  const { ref, isInView } = useSidebarInfiniteScroll();

  useEffect(() => {
    if (isInView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [isInView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // NOTE: Group keys are data-dependent (one per assistant, arriving page by
  // page), so the accordion is controlled the same way the date buckets are:
  // every known group is open unless the user collapsed it.
  const groupKeys = useMemo(() => groups.map((group) => group.key), [groups]);

  // NOTE: NSAI-5926 — collapsing an assistant's group survives a reload. The
  // hook owns only the keys rendered here and merges them into the record the
  // rest of the sidebar shares, so a group that has not been paged in yet
  // keeps whatever the user last chose for it.
  const { openSectionKeys, handleOpenSectionsChange } =
    useCollapsedSidebarSections({ sectionKeys: groupKeys });

  return (
    <>
      <Accordion
        type="multiple"
        value={openSectionKeys}
        onValueChange={handleOpenSectionsChange}
        className="w-full space-y-0.5"
      >
        {groups.map((group) => (
          <AccordionItem
            key={group.key}
            value={group.key}
            className="border-b-0"
            data-testid={`sidebar-assistant-chat-group-${group.key}`}
          >
            {/* NOTE: NSAI-6754 — `min-w-0` lets the trigger shrink below its
                content width; without it the flex item grows to fit a long
                assistant name, `truncate` never engages and the sidebar edge
                clips the text with no ellipsis. */}
            <AccordionTrigger
              className="min-w-0 text-sm font-normal text-foreground px-3 py-1 hover:no-underline justify-start gap-x-1.5"
              data-testid="sidebar-assistant-chat-group-trigger"
            >
              <AssistantAvatar
                assistantUuid={group.assistant?.uuid ?? group.key}
                assistantAvatar={
                  group.assistant
                    ? (assistantAvatarByUuid[group.assistant.uuid] ?? null)
                    : null
                }
                sizePx={ASSISTANT_GROUP_AVATAR_SIZE_PX}
              />
              <AssistantChatGroupLabel
                label={formatChatAssistantGroupLabel({ group, translate: t })}
              />
              {/* NOTE: NSAI-6259 follow-up — unlike the date buckets, EVERY
                  assistant group here can still grow: the page boundary is the
                  chat list's, not the assistant's, so a later page can add a
                  chat to any assistant already on screen. While a page is
                  outstanding they are all partial. */}
              <SidebarGroupCount
                count={group.chats.length}
                isPartial={hasNextPage}
              />
            </AccordionTrigger>
            <AccordionContent className="space-y-0.5">
              {group.chats.map((chat) => (
                <ChatListItem key={chat.uuid} chat={chat} />
              ))}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      <div ref={ref}>{isFetchingNextPage && <ChatListSkeleton />}</div>
    </>
  );
};

export const AssistantChatGroups = memo(AssistantChatGroupsInner);
